<template>
  <section class="language-switch-popup" aria-label="language switch popup">
    <header class="popup-head">
      <h3 class="popup-title">{{ popupTitle }}</h3>
      <button class="close-btn" type="button" @click="emit('close')">
        <SvgIcon name="svg/close.svg" className="close-icon" size="20" />
      </button>
    </header>

    <div class="language-list">
      <button
        v-for="item in languages"
        :key="item.value"
        type="button"
        :class="['lang-item', { active: item.value === currentLanguage }]"
        @click="handleSelect(item.value)">
        <span class="lang-main">
          <img class="flag" :src="item.img" :alt="item.name" />
          <span class="lang-name">{{ item.name }}</span>
        </span>

        <SvgIcon
          v-if="item.value === currentLanguage"
          name="svg/tick_green.svg"
          :fill="false"
          size="15"
          className="active-check" />
      </button>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { LangCode } from '@/types/config'
import SvgIcon from '@/components/SvgIcon.vue'
import { useSupportLanguages } from '@/composables/useSupportLanguages'

interface Props {
  title?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: ''
})

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { visibleLangList, currentLangCode, setLang } = useSupportLanguages()

const popupTitle = computed(() => props.title || t('Language'))
const languages = computed(() => visibleLangList.value)
const currentLanguage = computed(() => currentLangCode.value)

const handleSelect = (langCode: LangCode) => {
  setLang(langCode)
  emit('close')
}
</script>

<style scoped lang="scss">
:global(.language-switch-popup-wrapper) {
  align-items: flex-start !important;
  justify-content: flex-end !important;
  padding-top: 70px;
  padding-right: 50px;
  box-sizing: border-box;
}

:global(.language-switch-popup-content) {
  background: transparent !important;
  max-width: min(345px, calc(100vw - 20px)) !important;
  box-shadow: none !important;
  overflow: visible !important;
}

.language-switch-popup {
  width: min(345px, calc(100vw - 20px));
  border-radius: 10px;
  overflow: hidden;
  background: #141312;
  border: 1px solid rgba(99, 74, 37, 0.45);
}

.popup-head {
  height: 50px;
  padding: 0 12px 0 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #211f1b;
}

.popup-title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
  color: #e8ba6e;
}

.close-btn {
  width: 28px;
  height: 28px;
  border: 0;
  padding: 0;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: #e8ba6e;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(232, 186, 110, 0.12);
  }

  :deep(.close-icon) {
    width: 20px;
    height: 20px;
  }
}

.language-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 10px;
}

.lang-item {
  width: 100%;
  min-height: 40px;
  border: 0;
  border-radius: 10px;
  padding: 0 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: transparent;
  color: #fff;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(41, 38, 37, 0.6);
  }

  &.active {
    background: #292625;
  }
}

.lang-main {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.flag {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  object-fit: cover;
}

.lang-name {
  font-size: 14px;
  line-height: 1;
  font-weight: 400;
}

:deep(.active-check) {
  width: 15px;
  height: 15px;
}
</style>
